// ---------------------------------------------------------------------------
// WellKept shared vocabularies (mirror functions/lib/constants.ts)
// ---------------------------------------------------------------------------

import type { SizeSystem, SizeType } from './types';

export const SEASONS = [
  { id: 'spring', label: 'Spring' },
  { id: 'summer', label: 'Summer' },
  { id: 'fall', label: 'Fall' },
  { id: 'winter', label: 'Winter' },
] as const;

export const ITEM_CATEGORIES = [
  'Shirts',
  'T-Shirts',
  'Polo Shirts',
  'Sweaters & Knitwear',
  'Jackets & Blazers',
  'Outerwear & Coats',
  'Trousers',
  'Jeans',
  'Shorts',
  'Suits',
  'Shoes',
  'Sneakers',
  'Boots',
  'Accessories',
  'Watches',
  'Belts',
  'Bags',
  'Hats',
  'Sunglasses',
  'Ties & Pocket Squares',
  'Underwear & Socks',
  'Swimwear',
  'Activewear',
] as const;

// Category dropdown split: clothing/footwear above the divider, accessories below.
export const CLOTHING_CATEGORIES = [
  'Shirts', 'T-Shirts', 'Polo Shirts', 'Sweaters & Knitwear', 'Jackets & Blazers',
  'Outerwear & Coats', 'Suits', 'Trousers', 'Jeans', 'Shorts',
  'Shoes', 'Sneakers', 'Boots', 'Underwear & Socks', 'Swimwear', 'Activewear',
] as const;

export const ACCESSORY_CATEGORIES = [
  'Accessories', 'Bags', 'Watches', 'Belts', 'Hats', 'Sunglasses', 'Ties & Pocket Squares',
] as const;

export const BAG_SUBTYPES = [
  'Briefcase',
  'Backpack',
  'Tote',
  'Messenger Bag',
  'Duffle Bag',
  'Weekender',
  'Carry-On Luggage',
  'Checked Luggage',
  'Garment Bag',
  'Clutch',
  'Crossbody Bag',
  'Belt Bag',
  'Laptop Bag',
  'Travel Set',
  'Dopp Kit',
] as const;

export const MATERIALS = [
  'Cotton', 'Wool', 'Cashmere', 'Silk', 'Linen', 'Leather', 'Suede', 'Denim',
  'Polyester', 'Nylon', 'Velvet', 'Tweed', 'Flannel', 'Jersey', 'Canvas',
  'Merino', 'Alpaca', 'Mohair', 'Corduroy', 'Satin',
  // Exotic / specialty
  'Calfskin', 'Lambskin', 'Shearling', 'Alligator', 'Crocodile', 'Lizard',
  'Python', 'Ostrich', 'Stingray', 'Vicuña', 'Camel Hair', 'Horsehide',
  'Patent Leather', 'Nubuck',
] as const;

export const COLORS = [
  { name: 'Black', hex: '#000000' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Navy', hex: '#1a2744' },
  { name: 'Gray', hex: '#6B7280' },
  { name: 'Charcoal', hex: '#374151' },
  { name: 'Brown', hex: '#78350F' },
  { name: 'Tan', hex: '#D4A574' },
  { name: 'Camel', hex: '#C19A6B' },
  { name: 'Burgundy', hex: '#722F37' },
  { name: 'Red', hex: '#DC2626' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Orange', hex: '#EA580C' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Green', hex: '#16A34A' },
  { name: 'Olive', hex: '#556B2F' },
  { name: 'Teal', hex: '#0D9488' },
  { name: 'Blue', hex: '#2563EB' },
  { name: 'Light Blue', hex: '#7DD3FC' },
  { name: 'Purple', hex: '#7C3AED' },
  { name: 'Cream', hex: '#FFFDD0' },
  { name: 'Beige', hex: '#D9C5A0' },
] as const;

export const COLOR_HEX: Record<string, string> = Object.fromEntries(
  COLORS.map((c) => [c.name, c.hex]),
);

// Common tailoring / alteration actions. Free-form entries are also allowed.
export const TAILOR_ACTION_TYPES = [
  'Shortened',
  'Lengthened',
  'Waist taken in',
  'Waist let out',
  'Fix Jacket Roll',
  'Shorten Sleeves',
  'Customization',
] as const;

export const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'CNY', 'HKD'] as const;

// --- Sizing -----------------------------------------------------------------
// The DB stores a coarse size_type ('relative' | 'european' | 'shoe') because
// items.size_type has a CHECK constraint and the items table (FTS external
// content + cascading FKs) can't be rebuilt cheaply. The picker offers finer
// SizeSystems; they round-trip through the stored size_type + a suffix on the
// size value (see toStoredSizeType / formatStoredSize / parseStoredSize).

// Build an inclusive numeric size range as strings (supports 0.5 steps).
function sizeRange(min: number, max: number, step: number): string[] {
  const out: string[] = [];
  for (let v = min; v <= max + 1e-9; v += step) out.push(String(Math.round(v * 10) / 10));
  return out;
}

export const RELATIVE_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'] as const;
export const EUROPEAN_SIZES = ['44', '46', '48', '50', '52', '54', '56', '58', '60'] as const;
export const AMERICAN_SIZES = sizeRange(28, 38, 1);          // waist / numeric garment
export const SHOE_EU_SIZES = sizeRange(39, 47, 0.5);          // European shoe, half sizes
export const SHOE_US_SIZES = sizeRange(5, 12, 0.5);           // US shoe, half sizes
export const SHOE_UK_SIZES = sizeRange(5, 12, 0.5);           // UK shoe, half sizes

export const FOOTWEAR_CATEGORIES = ['Shoes', 'Sneakers', 'Boots'] as const;
export const isFootwearCategory = (category: string): boolean =>
  (FOOTWEAR_CATEGORIES as readonly string[]).includes(category);

export interface SizeSystemDef {
  id: SizeSystem;
  label: string;
}

// Which size systems a category can use (footwear gets shoe systems).
export function sizeSystemsForCategory(category: string): SizeSystemDef[] {
  if (isFootwearCategory(category)) {
    return [
      { id: 'shoe-eu', label: 'EU' },
      { id: 'shoe-us', label: 'US' },
      { id: 'shoe-uk', label: 'UK' },
    ];
  }
  return [
    { id: 'relative', label: 'Relative' },
    { id: 'european', label: 'European' },
    { id: 'american', label: 'American' },
  ];
}

// The value list a given system offers in the picker.
export function sizeOptionsForSystem(system: SizeSystem): readonly string[] {
  switch (system) {
    case 'european': return EUROPEAN_SIZES;
    case 'american': return AMERICAN_SIZES;
    case 'shoe-eu': return SHOE_EU_SIZES;
    case 'shoe-us': return SHOE_US_SIZES;
    case 'shoe-uk': return SHOE_UK_SIZES;
    case 'relative':
    default: return RELATIVE_SIZES;
  }
}

const SHOE_SUFFIX: Partial<Record<SizeSystem, string>> = {
  'shoe-eu': 'EU',
  'shoe-us': 'US',
  'shoe-uk': 'UK',
};

// Coarse bucket stored in items.size_type (satisfies the DB CHECK).
export function toStoredSizeType(system: SizeSystem): SizeType {
  if (system === 'shoe-eu' || system === 'shoe-us' || system === 'shoe-uk') return 'shoe';
  if (system === 'american') return 'european';
  return system; // 'relative' | 'european'
}

// The size string to persist. Shoe sizes carry an EU/US/UK suffix so the exact
// system survives a round-trip — the coarse size_type can't hold it, and US vs
// UK share the same 5–12 range. Also makes a bare "9.5" meaningful on display.
export function formatStoredSize(system: SizeSystem, value: string): string {
  const v = value.trim();
  if (!v) return '';
  const suffix = SHOE_SUFFIX[system];
  return suffix ? `${v} ${suffix}` : v;
}

// Recover the picker's system + raw value from a stored size_type/size/category.
// Ranges don't overlap, so legacy rows (no suffix) reconstruct correctly too.
export function parseStoredSize(
  sizeType: string | null,
  size: string | null,
  category: string,
): { system: SizeSystem; value: string } {
  const raw = (size ?? '').trim();
  const footwear = isFootwearCategory(category) || sizeType === 'shoe';
  if (footwear) {
    const m = raw.match(/^(.*?)\s*(EU|US|UK)$/i);
    if (m) {
      const sys = m[2].toUpperCase();
      const system: SizeSystem = sys === 'US' ? 'shoe-us' : sys === 'UK' ? 'shoe-uk' : 'shoe-eu';
      return { system, value: m[1].trim() };
    }
    // Legacy plain value: small numbers were US/UK-style, larger were EU (39–47).
    const n = parseFloat(raw);
    if (!isNaN(n) && n <= 20) return { system: 'shoe-us', value: raw };
    return { system: 'shoe-eu', value: raw };
  }
  if (sizeType === 'relative') return { system: 'relative', value: raw };
  const n = parseFloat(raw);
  if (!isNaN(n) && n >= 28 && n <= 38) return { system: 'american', value: raw };
  if (raw && isNaN(n)) return { system: 'relative', value: raw }; // stray letter size
  return { system: 'european', value: raw };
}

export const CARE_INSTRUCTIONS = [
  'Machine wash cold',
  'Machine wash warm',
  'Hand wash only',
  'Dry clean only',
  'Dry clean recommended',
  'Do not bleach',
  'Tumble dry low',
  'Hang dry',
  'Lay flat to dry',
  'Iron low heat',
  'Iron medium heat',
  'Do not iron',
  'Steam clean',
  'Spot clean only',
] as const;

export const OCCASIONS = [
  { id: 'work', label: 'Work' },
  { id: 'casual', label: 'Casual' },
  { id: 'date', label: 'Date Night' },
  { id: 'event', label: 'Event/Party' },
  { id: 'business', label: 'Business' },
  { id: 'travel', label: 'Travel' },
  { id: 'wedding', label: 'Wedding/Formal' },
  { id: 'other', label: 'Other' },
] as const;

export const FIT_SLOTS = [
  { id: 'top', label: 'Top', required: true },
  { id: 'bottom', label: 'Bottom', required: true },
  { id: 'shoes', label: 'Shoes', required: true },
  { id: 'jacket', label: 'Jacket', required: false },
  { id: 'outerwear', label: 'Outerwear', required: false },
  { id: 'accessory', label: 'Accessory', required: false },
] as const;

// Map a fit slot to the item categories that can fill it.
export const SLOT_CATEGORIES: Record<string, string[]> = {
  top: ['Shirts', 'T-Shirts', 'Polo Shirts', 'Sweaters & Knitwear', 'Activewear'],
  bottom: ['Trousers', 'Jeans', 'Shorts', 'Swimwear'],
  shoes: ['Shoes', 'Sneakers', 'Boots'],
  jacket: ['Jackets & Blazers', 'Suits'],
  outerwear: ['Outerwear & Coats'],
  accessory: ['Accessories', 'Watches', 'Belts', 'Bags', 'Hats', 'Sunglasses', 'Ties & Pocket Squares'],
};

export const SERVICE_TYPES = [
  'Dry Clean',
  'Laundry',
  'Alterations',
  'Shoe Repair',
  'Leather Care',
  'Watch Service',
  'Waterproofing',
  'Storage',
  'Other',
] as const;

export const SERVICE_PROVIDER_TYPES = [
  { id: 'dry-cleaner', label: 'Dry Cleaner' },
  { id: 'tailor', label: 'Tailor' },
  { id: 'cobbler', label: 'Cobbler' },
  { id: 'laundry', label: 'Laundry' },
  { id: 'leather', label: 'Leather Care' },
  { id: 'watch', label: 'Watch Service' },
  { id: 'storage', label: 'Storage' },
  { id: 'other', label: 'Other' },
] as const;

export const CLEANING_METHODS = [
  'Machine wash',
  'Hand wash',
  'Steam',
  'Spot clean',
  'Other',
] as const;

export const PLAN_TYPES = [
  { id: 'vacation', label: 'Vacation' },
  { id: 'business', label: 'Business Trip' },
  { id: 'event', label: 'Event' },
  { id: 'other', label: 'Other' },
] as const;

export const STATUS_LABELS: Record<string, string> = {
  'wishlist': 'Wishlist',
  'pending-delivery': 'Pending Delivery',
  'in-closet': 'In Closet',
  'out-for-service': 'Out for Service',
  'for-sale': 'For Sale',
  'archived': 'Archived',
};
